import { pieceById, PieceReference } from '@/components/piece/PieceReference'
import { CandidatePhoto } from '@/components/recovery/CandidatePhoto'
import {
  ABSENT_FACT,
  candidateStrengthLabels,
  pieceStateLabels,
  storedReasonText,
} from '@/lib/recovery/labels'
import type { RecoverySourceFact } from '@/lib/api/schemas/product-recovery.schema'
import type { RankedCandidate } from '@/lib/recovery/ranking'

/**
 * One recorded fact.
 *
 * An absent fact is a dash, never a blank cell and never a zero. Where the row
 * stores a reason the dash carries that sentence in its title; where nothing
 * stores one the dash stands alone rather than inventing an explanation.
 */
function Fact({
  name,
  label,
  value,
  reason = null,
  comparable = true,
}: {
  name: string
  label: string
  value: string | null
  reason?: string | null
  comparable?: boolean
}) {
  return (
    <div className="flex flex-wrap items-baseline gap-1">
      <dt className="text-ink-muted">{label}:</dt>
      {value === null ? (
        <dd
          className="text-ink-muted"
          data-fact={name}
          data-unavailable="true"
          data-reason={reason ?? undefined}
          title={reason === null ? undefined : storedReasonText(reason)}
        >
          {ABSENT_FACT}
        </dd>
      ) : (
        <dd data-fact={name} data-comparable={comparable ? undefined : 'false'} className="text-ink-2">
          {value}
          {comparable ? null : (
            <span className="ml-1 text-[10px] text-ink-muted">сравнить не с чем</span>
          )}
        </dd>
      )}
    </div>
  )
}

function weightText(grams: number | null): string | null {
  return grams === null ? null : `${grams.toLocaleString('ru-RU')} г`
}

/**
 * The source facts behind a candidate, each under its own kind.
 *
 * The kind is printed as the server spells it. A family this build has not seen
 * still reaches the operator under its own name rather than being given an
 * invented sentence, which is how the sibling surface reads a match reason.
 */
function SourceFacts({
  name,
  label,
  facts,
}: {
  name: string
  label: string
  facts: readonly RecoverySourceFact[]
}) {
  if (facts.length === 0) return null
  return (
    <div className="mt-2" data-facts={name}>
      <p className="text-[11px] text-ink-muted">{label}</p>
      <ul className="space-y-0.5 text-[11px] text-ink-muted">
        {facts.map((fact) => (
          <li key={`${fact.kind}:${fact.value}`} data-fact-kind={fact.kind}>
            {fact.kind}: <span className="font-mono">{fact.value}</span>
            {fact.sourceRef === null ? null : <> · {fact.sourceRef}</>}
          </li>
        ))}
      </ul>
    </div>
  )
}

/**
 * A candidate piece, state first.
 *
 * State leads because it settles 342 of the 743 ambiguous articles before anyone
 * compares anything: 207 hold exactly one piece recorded sold, and 135 hold none,
 * where the sale can be none of the candidates. Weight and size come next, since
 * they separate the pieces in 614 of those articles. The photograph comes last.
 */
/**
 * Which candidate facts can be held against the subject at all.
 *
 * The legacy row has no weight field in the contract, so a candidate weight is
 * never comparable: it separates candidates from each other and says nothing
 * about which one the subject is. Size is comparable only where the subject
 * recorded one. Two numbers side by side read as a scale whether or not a
 * footnote says otherwise, so the fact itself carries the mark.
 */
export type Comparability = { weight: boolean; size: boolean }

export function CandidateCard({
  ranked,
  comparability,
  selected,
  onSelect,
  disabled,
}: {
  ranked: RankedCandidate
  comparability: Comparability
  selected: boolean
  onSelect: (productId: string) => void
  disabled: boolean
}) {
  const { candidate, strength, indistinguishableFrom } = ranked
  const label = candidate.title ?? candidate.article ?? candidate.productId

  return (
    <li
      className={`rounded-lg border p-3 ${selected ? 'border-gold bg-gold/5' : 'border-rule bg-paper'}`}
      data-candidate={candidate.productId}
      data-strength={strength}
      data-piece-state={candidate.currentState ?? undefined}
      data-indistinguishable={indistinguishableFrom.length > 0 ? 'true' : undefined}
    >
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <span className="text-sm font-medium text-ink">
          {candidate.currentState === null ? (
            <span
              className="text-ink-muted"
              data-fact="state"
              data-unavailable="true"
              data-reason={candidate.currentStateUnavailableReason ?? undefined}
              title={
                candidate.currentStateUnavailableReason === null
                  ? undefined
                  : storedReasonText(candidate.currentStateUnavailableReason)
              }
            >
              {ABSENT_FACT}
            </span>
          ) : (
            <span data-fact="state">{pieceStateLabels[candidate.currentState]}</span>
          )}
        </span>
        <span className="rounded border border-rule px-1.5 py-0.5 text-[11px] text-ink-2" data-strength-label={strength}>
          {candidateStrengthLabels[strength]}
        </span>
      </div>

      <p className="mt-1 text-xs">
        <PieceReference piece={pieceById(candidate.productId, candidate.title ?? candidate.article)} originLabel="восстановлению привязки" />
      </p>

      <dl className="mt-2 grid gap-1 text-xs sm:grid-cols-2">
        <Fact
          name="weight"
          label="Вес"
          value={weightText(candidate.weightGrams)}
          reason={candidate.weightUnavailableReason}
          comparable={comparability.weight}
        />
        <Fact name="size" label="Размер" value={candidate.size} comparable={comparability.size} />
        <Fact name="series" label="Серия" value={candidate.seriesRef} />
        <Fact name="uin" label="УИН" value={candidate.uin} />
        {/* Shown only where the source said something else. Two identical lines
            teach an operator to skip both. */}
        {candidate.reportedUin === candidate.uin ? null : (
          <Fact name="reportedUin" label="УИН по источнику" value={candidate.reportedUin} />
        )}
        <Fact name="article" label="Артикул" value={candidate.article} />
      </dl>

      {indistinguishableFrom.length > 0 ? (
        <p className="mt-2 text-[11px] text-ink-muted" data-indistinguishable-count={indistinguishableFrom.length}>
          Ничем из записанного не отличается от другого кандидата: ни весом, ни размером.
        </p>
      ) : null}

      <SourceFacts name="source-fact" label="Идентификаторы источника" facts={candidate.sourceIdentities} />
      <SourceFacts name="evidence-fact" label="Доказательства" facts={candidate.evidence} />

      <div className="mt-2">
        <CandidatePhoto productId={candidate.productId} label={label} />
      </div>

      <div className="mt-3">
        <button
          type="button"
          className="btn btn-ghost text-xs"
          aria-pressed={selected}
          disabled={disabled}
          onClick={() => onSelect(candidate.productId)}
        >
          {selected ? 'Выбрано' : 'Выбрать это изделие'}
        </button>
      </div>
    </li>
  )
}
